import { z } from 'zod';

import { CountryCode } from '@src/common/auth';
import {
  buildPaymentCardParamsFromForm,
  isAcceptedCardBrand,
  isCardExpired,
  isValidBillingZipCode,
  isValidCardNumber,
  isValidCardValidity,
  isValidHolderIdentity,
  type PaymentCardParams,
  type PaymentOption,
} from '@src/domains/payments';
import type { Translate } from '@src/i18n/useTranslate';

type CardPaymentFormSchemaInput = {
  t: Translate['i18n'];
  isForeign: boolean;
  buyerCountryCode?: CountryCode;
  paymentOption?: PaymentOption;
};

export const cardPaymentFormSchema = ({
  t,
  isForeign,
  buyerCountryCode,
  paymentOption,
}: CardPaymentFormSchemaInput) => {
  const requiredMessage = t('direct_link_required_field');

  const requiredStringField = z.string({ required_error: requiredMessage }).nonempty({ message: requiredMessage });

  const requiredForExternalAuthenticationField = z
    .string()
    .nullable()
    .refine((field) => {
      if (!paymentOption?.requiresExternalAuthentication) {
        return true;
      }

      return Boolean(field?.trim());
    }, requiredMessage);

  const billingAddressObject = z.object({
    zipCode: z
      .string()
      .optional()
      .refine(
        (zipCode) => isValidBillingZipCode(zipCode, isForeign),
        t('direct_link_card_form_postal_code_must_be_valid'),
      ),
    state: requiredForExternalAuthenticationField,
    city: requiredForExternalAuthenticationField,
    district: requiredForExternalAuthenticationField,
    street: requiredForExternalAuthenticationField,
    number: requiredForExternalAuthenticationField,
    complement: z.string().nullable(),
  });

  return z
    .object({
      holder: requiredStringField,
      holderIdentity: z
        .string()
        .optional()
        .refine(
          (identity) => isValidHolderIdentity(identity, isForeign),
          t('direct_link_card_form_identity_must_be_valid'),
        ),
      cardNumber: requiredStringField
        .refine((number) => isValidCardNumber(number), t('direct_link_card_form_card_number_must_be_valid'))
        .refine(
          (number) => isAcceptedCardBrand(number, paymentOption),
          t('direct_link_card_form_card_brand_not_accepted'),
        ),
      securityCode: requiredStringField.min(3, t('direct_link_required_field')),
      validity: requiredStringField
        .refine((value) => isValidCardValidity(value), t('checkout_payment_card_validity_must_be_valid'))
        .refine(
          (value) => !isValidCardValidity(value) || !isCardExpired(value, new Date()),
          t('direct_link_card_form_card_expired'),
        ),
      installments: z.coerce.number().min(1),
      // The object carries the same condition as the fields inside it: without that, an absent
      // billingAddress skips their refinements and a 3DS payment validates with no address.
      billingAddress: paymentOption?.requiresExternalAuthentication
        ? billingAddressObject
        : billingAddressObject.optional(),
    })
    .transform(
      (data): PaymentCardParams =>
        buildPaymentCardParamsFromForm(data, isForeign, buyerCountryCode ?? CountryCode.BRAZIL),
    );
};

export type CardPaymentFormSchema = ReturnType<typeof cardPaymentFormSchema>;
export type CardFormValues = z.input<CardPaymentFormSchema>;
export type CardFormOutput = z.output<CardPaymentFormSchema>;
